import type { Terminal } from '@xterm/xterm';

/** xterm stdin is disabled for the iOS composer, so encode requested input here.
 * Mouse reports use legacy X10; the default Codex launcher uses inline scrollback. */
function encodeMouse(button: number, col: number, row: number): string {
  const clamp = (n: number) => Math.min(222, Math.max(0, n));
  return `\x1b[M${String.fromCharCode(32 + button)}${String.fromCharCode(
    32 + clamp(col) + 1,
  )}${String.fromCharCode(32 + clamp(row) + 1)}`;
}

/** Wheel up/down are reported as buttons 64/65. */
export function wheelReport(up: boolean, col: number, row: number): string {
  return encodeMouse(up ? 64 : 65, col, row);
}

export function clickReport(col: number, row: number): string {
  // Press then release, as a real terminal would send for a tap.
  return encodeMouse(0, col, row) + encodeMouse(3, col, row);
}

export type KeyName =
  | 'up' | 'down' | 'left' | 'right'
  | 'esc' | 'tab' | 'enter'
  | 'ctrl-c' | 'ctrl-d' | 'ctrl-t'
  | 'ctrl-l' | 'ctrl-r' | 'ctrl-u' | 'ctrl-z'
  | 'pgup' | 'pgdn';

/** Page buttons read local history when no application owns scrolling. */
export function scrollHistory(term: Pick<Terminal, 'modes' | 'buffer' | 'scrollPages'>, key: KeyName): boolean {
  if (key !== 'pgup' && key !== 'pgdn') return false;
  if (term.buffer.active.type !== 'normal' || term.modes.mouseTrackingMode !== 'none') return false;
  term.scrollPages(key === 'pgup' ? -1 : 1);
  return true;
}

/**
 * Arrow keys change shape depending on DECCKM (`?1h`), when an application enables it:
 * in application mode they are ESC O A, in normal mode ESC [ A. Sending the wrong
 * one gets silently ignored. xterm tracks the mode for us, so ask it rather than
 * guessing — the answer differs between Codex and the shell you drop back into.
 */
export function keySeq(term: Terminal, key: KeyName): string {
  const app = term.modes.applicationCursorKeysMode;
  const arrow = (letter: string) => (app ? `\x1bO${letter}` : `\x1b[${letter}`);
  switch (key) {
    case 'up':
      return arrow('A');
    case 'down':
      return arrow('B');
    case 'right':
      return arrow('C');
    case 'left':
      return arrow('D');
    case 'esc':
      return '\x1b';
    case 'tab':
      return '\t';
    case 'enter':
      return '\r';
    case 'ctrl-c':
      return '\x03';
    case 'ctrl-d':
      return '\x04';
    case 'ctrl-t':
      return '\x14';
    case 'ctrl-l':
      return '\x0c';
    case 'ctrl-r':
      return '\x12';
    case 'ctrl-u':
      return '\x15';
    case 'ctrl-z':
      return '\x1a';
    case 'pgup':
      return '\x1b[5~';
    case 'pgdn':
      return '\x1b[6~';
  }
}

/** Frame multiline prompts as a paste when the running TUI requests that mode. */
export function promptInput(term: Pick<Terminal, 'modes'>, text: string): string {
  const paste = term.modes.bracketedPasteMode ? `\x1b[200~${text}\x1b[201~` : text;
  return paste + '\r';
}
